import type { AdminStory, Brand, Category, Item, StoryExpiryMode, StoryLinkType } from '@erp/shared';
import { ArrowLeft, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Input } from '../../components/shadcn/index.js';
import { Badge, Button, Combobox, type ComboboxOption, FormField } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { flattenCategoryOptions } from '../items/itemHelpers.js';

import { archiveStory, getStory, publishStory, setStoryProducts, updateStory } from './api.js';
import { MediaManager } from './MediaManager.js';
import { displayStatus, statusTone } from './status.js';
import { StoryPreview } from './StoryPreview.js';

/** ISO → value for <input type="datetime-local"> in the browser's local zone. */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function StoryEditor({ story: initial, onBack, onChanged }: { story: AdminStory; onBack: () => void; onChanged: () => void }): JSX.Element {
  const { t } = useTranslation('stories');
  const { t: tc } = useTranslation();
  const canManage = usePermission('stories.manage');
  const [story, setStory] = useState<AdminStory>(initial);
  const [title, setTitle] = useState(initial.title ?? '');
  const [caption, setCaption] = useState(initial.caption ?? '');
  const [expiry, setExpiry] = useState<StoryExpiryMode | 'schedule'>(
    initial.scheduledAt && new Date(initial.scheduledAt).getTime() > Date.now() ? 'schedule' : initial.expiresAt ? '24h' : 'permanent',
  );
  const [scheduleAt, setScheduleAt] = useState(isoToLocalInput(initial.scheduledAt));
  const [items, setItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [linkType, setLinkType] = useState<StoryLinkType | null>(initial.linkType ?? null);
  const [linkRefId, setLinkRefId] = useState<string | null>(initial.linkRefId ?? null);
  const [saving, setSaving] = useState(false);

  const reload = async (): Promise<void> => { const s = await getStory(story.id); setStory(s); onChanged(); };

  useEffect(() => {
    void api().get<{ items: Item[] }>('/items', { params: { limit: 500 } }).then((r) => setItems(r.data.items)).catch(() => undefined);
    void api().get<{ items: Category[] }>('/categories').then((r) => setCategories(r.data.items)).catch(() => undefined);
    void api().get<{ items: Brand[] }>('/brands').then((r) => setBrands(r.data.items)).catch(() => undefined);
  }, []);

  const itemOptions = useMemo<ComboboxOption[]>(
    () => items.filter((i) => i.isActive).map((i) => ({ value: i.id, label: `${i.code} — ${i.name}`, sublabel: i.barcode ?? undefined, keywords: `${i.code} ${i.name} ${i.barcode ?? ''}` })),
    [items],
  );
  const categoryOptions = useMemo<ComboboxOption[]>(() => flattenCategoryOptions(categories).map((c) => ({ value: c.id, label: c.label })), [categories]);
  const brandOptions = useMemo<ComboboxOption[]>(() => brands.map((b) => ({ value: b.id, label: b.title })), [brands]);
  const taggedIds = story.products.map((p) => p.itemId);

  // Persist the story→entity link. A bare type change (no target yet) only updates
  // local state; picking a target or clearing to "None" is written through.
  const onLinkChange = async (type: StoryLinkType | null, refId: string | null): Promise<void> => {
    setLinkType(type); setLinkRefId(refId);
    if (type === null) { await updateStory(story.id, { linkType: null, linkRefId: null }); onChanged(); }
    else if (refId) { await updateStory(story.id, { linkType: type, linkRefId: refId }); onChanged(); }
  };

  const persistDetails = async (): Promise<void> => {
    if ((title.trim() || null) !== story.title || (caption.trim() || null) !== story.caption) {
      await updateStory(story.id, { title: title.trim() || null, caption: caption.trim() || null });
    }
  };

  const addProduct = async (itemId: string | undefined): Promise<void> => {
    if (!itemId || taggedIds.includes(itemId)) return;
    const s = await setStoryProducts(story.id, [...taggedIds, itemId]);
    setStory(s); onChanged();
  };
  const removeProduct = async (itemId: string): Promise<void> => {
    const s = await setStoryProducts(story.id, taggedIds.filter((x) => x !== itemId));
    setStory(s); onChanged();
  };

  const onSaveDraft = async (): Promise<void> => {
    setSaving(true);
    try { await persistDetails(); await reload(); toast.success(t('publish.saved', { defaultValue: 'Saved' })); }
    finally { setSaving(false); }
  };
  const onPublish = async (): Promise<void> => {
    setSaving(true);
    try {
      await persistDetails();
      const input = expiry === 'schedule'
        ? { expiry: '24h' as StoryExpiryMode, scheduledAt: scheduleAt ? new Date(scheduleAt).toISOString() : null }
        : { expiry: expiry as StoryExpiryMode, scheduledAt: null };
      // For a plain "schedule", default to permanent-at-go-live unless the admin
      // also wants 24h; keep it simple: schedule always uses 24h expiry off go-live.
      if (expiry === 'schedule') input.expiry = '24h';
      await publishStory(story.id, input);
      await reload();
      toast.success(expiry === 'schedule' ? t('publish.scheduled', { defaultValue: 'Story scheduled' }) : t('publish.published', { defaultValue: 'Story published' }));
    } finally { setSaving(false); }
  };
  const onArchive = async (): Promise<void> => {
    setSaving(true);
    try { await archiveStory(story.id); await reload(); toast.success(t('publish.archived', { defaultValue: 'Story archived' })); }
    finally { setSaving(false); }
  };

  const ds = displayStatus(story);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} aria-label={tc('actions.back', { defaultValue: 'Back' })}>
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h1 className="text-xl font-semibold">{story.title || t('stories.edit', { defaultValue: 'Edit story' })}</h1>
        <Badge tone={statusTone(ds)}>{t(`stories.status.${ds}`, { defaultValue: ds })}</Badge>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_260px]">
        <div className="flex flex-col gap-4">
          <Card className="flex flex-col gap-4 p-5">
            <FormField id="st-title" label={t('stories.titleField', { defaultValue: 'Title (optional)' })}>
              <Input id="st-title" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => void persistDetails()} maxLength={200} placeholder={t('stories.titlePlaceholder', { defaultValue: 'e.g. New Arrivals' })} disabled={!canManage} />
            </FormField>
            <FormField id="st-caption" label={t('stories.caption', { defaultValue: 'Caption (optional)' })}>
              <Input id="st-caption" value={caption} onChange={(e) => setCaption(e.target.value)} onBlur={() => void persistDetails()} maxLength={1000} placeholder={t('stories.captionPlaceholder', { defaultValue: 'A short line shown over the story' })} disabled={!canManage} />
            </FormField>
          </Card>

          <Card className="p-5">
            <MediaManager storyId={story.id} media={story.media} onChange={reload} />
          </Card>

          <Card className="flex flex-col gap-3 p-5">
            <div>
              <div className="text-sm font-medium">{t('products.title', { defaultValue: 'Tagged products' })}</div>
              <p className="text-xs text-fg-muted">{t('products.hint', { defaultValue: 'Tap-through products shown on the story.' })}</p>
            </div>
            {canManage && (
              <Combobox options={itemOptions.filter((o) => !taggedIds.includes(o.value))} value={undefined} onChange={(v) => void addProduct(v)} clearOnSelect placeholder={t('products.search', { defaultValue: 'Search products…' })} />
            )}
            {story.products.length === 0 ? (
              <div className="text-sm text-fg-muted">{t('products.empty', { defaultValue: 'No products tagged' })}</div>
            ) : (
              <div className="flex flex-col gap-1">
                {story.products.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-1.5">
                    <span className="truncate text-sm">{p.code ? `${p.code} — ` : ''}{p.name ?? p.itemId}</span>
                    {canManage && (
                      <button type="button" onClick={() => void removeProduct(p.itemId)} className="text-fg-muted hover:text-danger" aria-label={t('products.remove', { defaultValue: 'Remove' })}>
                        <X className="h-4 w-4" aria-hidden />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {canManage && (
            <Card className="flex flex-col gap-3 p-5">
              <LinkTargetPicker
                productOptions={itemOptions}
                categoryOptions={categoryOptions}
                brandOptions={brandOptions}
                type={linkType}
                refId={linkRefId}
                onChange={(ty, ref) => void onLinkChange(ty, ref)}
              />
            </Card>
          )}

          {canManage && (
            <Card className="flex flex-col gap-3 p-5">
              <div className="text-sm font-medium">{t('publish.title', { defaultValue: 'Visibility' })}</div>
              <fieldset className="flex flex-col gap-2">
                {(['permanent', '24h', 'schedule'] as const).map((mode) => (
                  <label key={mode} className="flex items-start gap-2 text-sm">
                    <input type="radio" name="expiry" className="mt-1" checked={expiry === mode} onChange={() => setExpiry(mode)} />
                    <span>
                      <span className="font-medium">{t(`publish.${mode === '24h' ? 'h24' : mode}`, { defaultValue: mode })}</span>
                      <span className="block text-xs text-fg-muted">{t(`publish.${mode === '24h' ? 'h24Hint' : `${mode}Hint`}`, { defaultValue: '' })}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              {expiry === 'schedule' && (
                <FormField id="st-sched" label={t('publish.scheduleAt', { defaultValue: 'Go-live time' })}>
                  <Input id="st-sched" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
                </FormField>
              )}
              <div className="flex flex-wrap gap-2">
                <Button loading={saving} onClick={() => void onPublish()} disabled={story.media.length === 0}>
                  {expiry === 'schedule' ? t('publish.schedule', { defaultValue: 'Schedule' }) : t('publish.publishNow', { defaultValue: 'Publish' })}
                </Button>
                <Button variant="secondary" loading={saving} onClick={() => void onSaveDraft()}>{t('publish.saveDraft', { defaultValue: 'Save draft' })}</Button>
                {story.status !== 'archived' && (
                  <Button variant="ghost" loading={saving} onClick={() => void onArchive()}>{t('publish.archive', { defaultValue: 'Archive' })}</Button>
                )}
              </div>
            </Card>
          )}
        </div>

        <div className="lg:sticky lg:top-4">
          <div className="mb-2 text-sm font-medium">{t('preview.title', { defaultValue: 'Preview' })}</div>
          <StoryPreview story={story} />
        </div>
      </div>
    </div>
  );
}

const LINK_TYPES: Array<StoryLinkType | 'none'> = ['none', 'product', 'category', 'brand'];

/**
 * "Link to" control shared by the story + highlight editors: a None/Product/
 * Category/Brand selector feeding one Combobox from the matching source. Emits
 * (type, refId); a bare type switch sends refId=null so the parent knows the
 * target is not yet chosen (and shouldn't persist a half link).
 */
export function LinkTargetPicker({
  productOptions,
  categoryOptions,
  brandOptions,
  type,
  refId,
  onChange,
}: {
  productOptions: ComboboxOption[];
  categoryOptions: ComboboxOption[];
  brandOptions: ComboboxOption[];
  type: StoryLinkType | null;
  refId: string | null;
  onChange: (type: StoryLinkType | null, refId: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation('stories');
  const options = type === 'product' ? productOptions : type === 'category' ? categoryOptions : type === 'brand' ? brandOptions : [];
  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="text-sm font-medium">{t('link.title', { defaultValue: 'Link to' })}</div>
        <p className="text-xs text-fg-muted">{t('link.hint', { defaultValue: 'Send viewers to a product, category, or brand.' })}</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {LINK_TYPES.map((ty) => {
          const active = ty === 'none' ? type === null : type === ty;
          return (
            <button
              key={ty}
              type="button"
              onClick={() => onChange(ty === 'none' ? null : ty, null)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${active ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:bg-bg-subtle'}`}
            >
              {t(`link.${ty}`, { defaultValue: ty })}
            </button>
          );
        })}
      </div>
      {type && (
        <Combobox
          options={options}
          value={refId ?? undefined}
          onChange={(v) => onChange(type, v ?? null)}
          placeholder={t('link.pick', { defaultValue: 'Choose target…' })}
        />
      )}
    </div>
  );
}
